import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, Eye, EyeOff, Loader2, Building2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function PortalLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('portal_token', data.access_token);
        localStorage.setItem('portal_refresh_token', data.refresh_token);
        localStorage.setItem('portal_user', JSON.stringify(data.user));
        
        toast.success('Login realizado com sucesso!');
        
        // Use replace to prevent back button issues
        // Check if must change password
        if (data.user.must_change_password) {
          navigate('/portal/alterar-password', { replace: true });
        } else {
          navigate('/portal/dashboard', { replace: true });
        }
      } else {
        toast.error(data.detail || 'Erro no login');
      }
    } catch (error) {
      toast.error('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="portal-login-page">
      {/* Left side - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0066CC] to-[#004499] p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-3 text-white hover:opacity-80 transition">
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar ao site</span>
          </Link>
        </div>
        
        <div className="text-white">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Portal das Instituições</h1>
              <p className="text-white/70">ENTRAJUDATA</p>
            </div>
          </div>
          
          <h2 className="text-4xl font-bold mb-6">
            Gerir a sua<br />
            <span className="text-green-400">instituição</span> nunca<br />
            foi tão fácil
          </h2>
          
          <div className="space-y-4 text-white/80">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">✓</div>
              <span>Atualize dados da instituição</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">✓</div>
              <span>Submeta pedidos de recursos 24/7</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">✓</div>
              <span>Gerir documentos e voluntariado</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">✓</div>
              <span>Acompanhe histórico de apoios</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3 text-white/60">
          <Heart className="w-5 h-5 text-red-400" />
          <span>ENTRAJUDA - Associação para o Apoio a Instituições de Solidariedade Social</span>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile header */}
          <div className="lg:hidden mb-8 text-center">
            <div className="w-16 h-16 bg-[#0066CC] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Portal das Instituições</h1>
            <p className="text-slate-500">ENTRAJUDATA</p>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl">Entrar</CardTitle>
              <CardDescription>
                Aceda à área reservada da sua instituição
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@instituicao.pt"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="portal-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="portal-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-slate-300" />
                    <span className="text-slate-600">Lembrar-me</span>
                  </label>
                  <Link to="/portal/recuperar-password" className="text-[#0066CC] hover:underline">
                    Esqueci a password
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#0066CC] hover:bg-[#0055AA]"
                  disabled={loading}
                  data-testid="portal-login-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A entrar...
                    </>
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-500">
                  Ainda não tem conta?{' '}
                  <Link to="/portal/registar" className="text-[#0066CC] hover:underline font-medium">
                    Registe a sua instituição
                  </Link>
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-xs text-center text-slate-400">
                  Precisa de ajuda?{' '}
                  <a href="mailto:suporte@entrajuda.pt" className="text-[#0066CC] hover:underline">
                    suporte@entrajuda.pt
                  </a>
                  {' '}|{' '}
                  <a href="tel:213600500" className="text-[#0066CC] hover:underline">
                    213 600 500
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-slate-400 mt-6">
            © {new Date().getFullYear()} ENTRAJUDA. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
}
